<template>
  <div class="d-flex fl-col fl-s-b aside-wrapper" data-cy="aside-order-summary">
    <div class="order-summary-wrapper d-flex fl-col fl-s-b scroll-y">
      <div class="scroll-y order-summary d-flex fl-col fl-s-b">
        <div class="upper-part-container d-flex flex-row fl-s-b">
          <div class="title d-flex fl-col fl-j-c">
            <p
              :class="{
                'long-title': isLongTitle,
                'max-content-width': !isLongTitle,
              }"
            >
              {{ t("asideordersummary.title") }}
            </p>
            <div class="order-reference-wrapper">
              <p class="order-reference" data-cy="order-reference">
                {{ t("orders.reference") }}: {{ selectedOrder?.reference }}
              </p>
              <SBadge
                v-if="openBalance > 0"
                variant="destructive"
                data-cy="order-unpaid-badge"
              >
                {{ t("order.unpaid") }}
              </SBadge>
              <div class="clipboard">
                <SvgIcon
                  :iconContent="copyIcon"
                  iconStyle="width: 12px; height: 12px; cursor:pointer;"
                  @click="copyToClipboard(selectedOrder?.reference || '')"
                />
                <SvgIcon
                  v-if="showCopyCheck"
                  :iconContent="check"
                  iconStyle="width: 12px; height: 12px; cursor:pointer"
                  class="check"
                />
              </div>
            </div>
          </div>
          <div
            v-if="showPrintBill"
            class="print-receipt-btn-container d-flex flex-row fl-s-b flex-align-center"
            @click="printBill()"
            data-cy="print-bill-btn"
          >
            <div class="icon-container">
              <SvgIcon
                :iconContent="printIcon"
                :iconAlt="printAlt"
                :iconStyle="iconStyle"
              />
            </div>
            <p class="max-content-width">
              {{ t("completeorder.print") }}
            </p>
          </div>
          <div
            class="assign-customer-btn-container d-flex flex-row fl-s-b flex-align-center"
            data-cy="assign-customer-button"
            @click="onClickCustomers"
          >
            <div class="icon-container">
              <SvgIcon
                :iconContent="iconContent"
                :iconAlt="iconAlt"
                :iconStyle="iconStyle"
              />
            </div>
            <p class="max-content-width">
              {{ getCustomerFullName }}
            </p>
          </div>
        </div>

        <!-- Waiver section — shown in the completed step for waiver-enabled groups -->
        <WaiverSection
          v-if="isInCompletedStep && isWaiverAllowed"
          :linkedCount="linkedCount"
          :isLoadingCount="isLoadingCount"
        />

        <div class="pss-list-container d-flex fl-col fl-s-a">
          <div
            v-for="orderLine in orderSummary.orderLines"
            :key="orderLine.id"
            :class="
              orderLine.lineType === LineType.CancelLine
                ? 'cancelled-pss-container'
                : ''
            "
          >
            <!-- <div v-if="orderLine.quantity !== 0"> -->
            <div class="pss-container d-flex flex-row fl-s-b">
              <div class="ticket-name-container">
                <p class="ticket-name">
                  {{ getOrderLineDisplayName(orderLine) }}
                </p>
                <p class="price-name" v-if="shouldShowTicketInfo(orderLine)">
                  {{ orderLine.priceName }}
                </p>
                <p
                  class="timeslot"
                  v-if="shouldShowTicketInfo(orderLine) && orderLine.timeSlotId"
                >
                  {{ orderLineTimeslots.get(orderLine.timeSlotId) }}
                </p>
                <p
                  v-if="orderLine.seats && orderLine.seats.length > 0"
                  class="seat"
                >
                  {{
                    orderLine.seats.length > 1
                      ? `${t("field.seat")}: ${orderLine.seats
                          .map((seat: OrderLineSeat) => seat.seatLabel)
                          .join(", ")}`
                      : `${t("field.seat")}: ${orderLine.seats[0].seatLabel}`
                  }}
                </p>
                <p
                  v-for="line in getOrderLineDiscountLines(orderLine)"
                  :key="line"
                  class="ticket-discount-info"
                >
                  {{ line }}
                </p>
              </div>
              <div class="quantity-container">
                <p
                  class="quantity"
                  :class="
                    orderLine.lineType === LineType.CancelLine
                      ? 'erased-tickets'
                      : ''
                  "
                >
                  X{{ Math.abs(orderLine.quantity) }}
                </p>
                <Trash2Icon
                  v-if="shouldShowRemoveBtn(orderLine)"
                  class="remove-btn"
                  @click="handleRemoveOrderLine(orderLine)"
                  data-cy="remove-pss-button"
                />
              </div>
              <div class="unit-price-container">
                <p
                  class="unit-price"
                  :class="
                    orderLine.lineType === LineType.CancelLine
                      ? 'erased-tickets'
                      : ''
                  "
                >
                  {{
                    (orderLine.overridePrice ?? orderLine.unitPrice).toFixed(2)
                  }}
                </p>
              </div>
              <div
                class="total-price-container"
                :class="{
                  'with-pt': getOrderLineDiscountLines(orderLine).length > 0,
                }"
              >
                <p
                  class="total-price"
                  :class="{
                    'erased-tickets':
                      orderLine.lineType === LineType.CancelLine ||
                      getOrderLineDiscountLines(orderLine).length > 0,
                  }"
                >
                  {{ orderLine.totalPrice.toFixed(2) }}
                </p>
                <p
                  v-if="
                    getOrderLineDiscountLines(orderLine).length > 0 &&
                    orderLine.lineType !== LineType.CancelLine
                  "
                  class="total-price"
                >
                  {{ getOrderLineDiscountedTotal(orderLine).toFixed(2) }}
                </p>
              </div>
            </div>
            <div
              v-if="orderLine.state === OrderSessionState.COMPLETED"
              class="tickets-status-container"
            >
              <p>
                {{ getLineStatusTranslation(orderLine) }}
              </p>
            </div>
            <!-- </div> -->
          </div>

          <!-- Discount -->
          <el-row
            v-if="isInPaymentStep && !!orderSummary.totalDiscounts"
            class="total-container discount-container"
            justify="start"
          >
            <el-col :span="5" class="total-container">
              {{ `${t("button.discount")}:` }}
            </el-col>
            <el-col :span="5" class="total-container d-flex fl-a-c fl-j-e">
              {{ currency }}
            </el-col>
            <el-col :span="11" class="d-flex flex-row fl-a-c fl-j-e"> </el-col>
            <el-col class="total-container text-right" :span="3">
              {{ orderSummary.totalDiscounts.toFixed(2) }}
            </el-col>
          </el-row>
        </div>

        <!-- Subtotal -->
        <div
          v-if="isInPaymentStep || isInCompletedStep"
          class="w-full d-flex flex-1 fl-s-e fl-a-c"
        >
          <div class="total-container text-left" style="width: 33%">
            {{ `${t("field.subtotal")}:` }}
          </div>
          <div class="total-container text-center" style="width: 33%">
            {{ currency }}
          </div>
          <div class="total-container text-right" style="width: 33%">
            {{ orderSummary.subtotal.toFixed(2) }}
          </div>
        </div>

        <!-- Tax -->
        <div
          v-if="isInPaymentStep || isInCompletedStep"
          class="w-full d-flex flex-1 fl-s-e fl-a-c"
        >
          <div class="total-container text-left" style="width: 33%">
            {{ `${t("field.vat")}:` }}
          </div>
          <div class="total-container text-center" style="width: 33%">
            {{ currency }}
          </div>
          <div class="total-container text-right" style="width: 33%">
            {{ orderSummary.totalVat.toFixed(2) }}
          </div>
        </div>

        <!-- Total -->
        <div
          v-if="(isInPaymentStep || isInCompletedStep) && !isOrderEdited"
          class="w-full d-flex flex-1 fl-s-e fl-a-c"
        >
          <div class="total-container text-left" style="width: 33%">
            {{ `${t("field.total")}:` }}
          </div>
          <div class="total-container text-center" style="width: 33%">
            {{ currency }}
          </div>
          <div class="total-container text-right" style="width: 33%">
            {{ orderSummary.totalWithDiscount.toFixed(2) }}
          </div>
        </div>

        <!-- Total - multi currencies -->
        <div
          v-if="
            (isInPaymentStep || isInCompletedStep) &&
            !isOrderEdited &&
            orderSummary.payments.length
          "
        >
          <div
            v-for="(currency, index) in currenciesUsedInPayment"
            :key="index"
          >
            <div
              v-if="currency && currency.label !== groupCurrency.label"
              class="w-full d-flex flex-1 fl-s-e fl-a-c"
            >
              <div class="total-container text-left" style="width: 33%">
                {{ `${t("field.total")}:` }}
              </div>
              <div class="total-container text-center p-rel" style="width: 33%">
                {{ currency.label }}

                <span style="position: absolute; right: -1rem"
                  >R {{ currency.exchangeRate }}</span
                >
              </div>
              <div class="total-container text-right" style="width: 33%">
                {{
                  (
                    orderSummary.totalWithDiscount * currency.exchangeRate
                  ).toFixed(2)
                }}
              </div>
            </div>
          </div>
        </div>

        <!-- SHOWING CASH AMOUNT (REMOVABLE) -->
        <!-- Total - Edit Order -->
        <div v-if="isOrderEdited" class="w-full d-flex flex-1 fl-s-e fl-a-c">
          <div class="total-container text-left" style="width: 33%">
            {{ `${t("field.total")}:` }}
          </div>
          <div class="total-container text-center" style="width: 33%">
            {{ currency }}
          </div>
          <div class="total-container text-right" style="width: 33%">
            {{ orderSummary.totalWithDiscount.toFixed(2) }}
          </div>
        </div>

        <!-- FIS-719: debt left open by a previous visit, collected with this
             one; on the Complete step, what is still owed on the order -->
        <div
          v-if="openBalanceRowAmount > 0"
          class="w-full d-flex flex-1 fl-s-e fl-a-c"
          data-cy="open-balance-previous-visit"
        >
          <div class="total-container text-left" style="width: 33%">
            {{
              `${t(
                isInCompletedStep
                  ? "field.remaining-balance"
                  : "field.open-balance-previous-visit"
              )}:`
            }}
          </div>
          <div class="total-container text-center" style="width: 33%">
            {{ currency }}
          </div>
          <div class="total-container text-right" style="width: 33%">
            {{ openBalanceRowAmount.toFixed(2) }}
          </div>
        </div>

        <!-- Pending voucher payments (from `orderSummary.vouchers`) -->
        <div v-if="pendingVoucherPayments.length" style="padding-top: 40px">
          <div
            v-for="voucher in pendingVoucherPayments"
            :key="voucher.id"
            class="d-flex amounts-paid align-items-center"
            style="min-height: 25px"
          >
            <div style="width: 33%">
              <p>{{ voucher.label }}</p>
              <p v-if="voucher.showPendingHint" class="pending-payment-hint">
                Pending Payment
              </p>
            </div>
            <Trash2Icon
              v-if="shouldShowRemoveVoucherBtn()"
              style="margin-left: 5px"
              class="remove-btn"
              data-cy="remove-pending-voucher-btn"
              @click="removeVoucherPayment(voucher.id)"
            />
            <div class="text-center" style="width: 33%">
              <p>{{ voucher.currency }}</p>
            </div>
            <div class="text-right" style="width: 33%">
              <p>{{ voucher.amount.toFixed(2) }}</p>
            </div>
          </div>
        </div>

        <!-- Payment amounts (filtered: completed + pending Cash / Swiss QR) -->
        <div v-if="displayedPayments.length" style="padding-top: 40px">
          <div
            v-for="payment in displayedPayments"
            :key="payment.id"
            class="d-flex amounts-paid align-items-center"
            style="min-height: 25px"
          >
            <div style="width: 33%">
              <p>
                <!-- Online methods (VISA, TWINT, ...) have no POS translation
                     entry — fall back to the raw method name -->
                {{
                  PaymentMethodToTranslatedString[payment.paymentMethod] ||
                  String(payment.paymentMethod).replace(/_/g, " ")
                }}
              </p>
            </div>
            <!-- Remove button: hidden for Adyen, for previously-completed
                 sessions, and in the completed-order step. -->
            <Trash2Icon
              v-if="shouldShowRemovePaymentBtn(payment)"
              style="margin-left: 5px"
              class="remove-btn"
              @click="handleRemovePayment(payment)"
            />
            <div class="text-center" style="width: 33%">
              <p>
                {{ payment.multiCurrencyInfo?.paymentCurrency || currency }}
              </p>
            </div>
            <div class="text-right" style="width: 33%">
              <p>
                {{
                  (
                    (payment.multiCurrencyInfo?.providedCashBill ||
                      payment.value) / 100
                  ).toFixed(2)
                }}
              </p>
            </div>
          </div>
        </div>

        <!-- Total Paid & Total Refunded -->
        <!-- SHOW WHAT WAS PAID WITH BOOKINGS ACCEPTING SPLIT PAYMENT (BASED ON CONFIRMED ACCOUNTINGS) -->
        <div
          style="border-top: 2px solid white"
          v-if="displayedPayments.length || pendingVoucherPayments.length"
        >
          <div class="d-flex flex-1 fl-s-e fl-a-c" style="margin-bottom: -15px">
            <div class="confirmed-amount-total text-left" style="width: 33%">
              {{ t("asideordersummary.amount-confirmed") }}
            </div>
            <div class="confirmed-amount-total text-center" style="width: 33%">
              {{ currency }}
            </div>
            <div class="confirmed-amount-total text-right" style="width: 33%">
              {{ totalConfirmedAmount.toFixed(2) }}
            </div>
          </div>

          <!-- FIS-715: a terminal gratuity. Kept out of "Total paid" on purpose —
               it settles nothing, so the balance above stays the amount owed. -->
          <div
            class="d-flex flex-1 fl-s-e fl-a-c"
            style="margin-bottom: -15px"
            v-if="!!orderSummary.totalTips"
            data-cy="order-summary-tip"
          >
            <div class="confirmed-amount-total text-left" style="width: 33%">
              {{ t("field.tip") }}
            </div>
            <div class="confirmed-amount-total text-center" style="width: 33%">
              {{ currency }}
            </div>
            <div
              class="confirmed-amount-total text-right"
              style="width: 33%"
              data-cy="order-summary-tip-amount"
            >
              <!-- totalTips is in MINOR units, like totalPayments. -->
              {{ (orderSummary.totalTips / 100).toFixed(2) }}
            </div>
          </div>

          <div
            class="d-flex flex-1 fl-s-e fl-a-c"
            style="margin-bottom: -15px"
            v-if="
              isOrderEdited ||
              (isInCompletedStep && !!orderSummary.totalRefunds)
            "
          >
            <div class="confirmed-amount-total text-left" style="width: 33%">
              {{ t("asideordersummary.amount-refunded") }}
            </div>
            <div class="confirmed-amount-total text-center" style="width: 33%">
              {{ currency }}
            </div>
            <div class="confirmed-amount-total text-right" style="width: 33%">
              <!-- totalRefunds sums negative payment values; display it positive. -->
              {{ Math.abs(orderSummary.totalRefunds / 100).toFixed(2) }}
            </div>
          </div>

          <!-- Belgian cash rounding, negated so it reads as an adjustment
               against the amount the customer handed over -->
          <!-- <div
            v-if="roundingTotal"
            class="d-flex flex-1 fl-s-e fl-a-c"
            style="margin-bottom: -15px"
          >
            <div class="confirmed-amount-total text-left" style="width: 33%">
              {{ $t("payment-method.CASH_ROUNDING") }}
            </div>
            <div class="confirmed-amount-total text-center" style="width: 33%">
              {{ payingCurrency }}
            </div>
            <div class="confirmed-amount-total text-right" style="width: 33%">
              {{ ((roundingTotal / 100) * -1).toFixed(2) }}
            </div>
          </div> -->
        </div>
      </div>

      <div>
        <div class="accountings-container d-flex flex-row fl-s-b">
          <p class="title d-flex fl-col fl-j-c">
            {{ t("asideordersummary.balance-in") }}
            {{ currency }}
          </p>
          <p class="value d-flex fl-col fl-j-c">
            {{ amountDue.toFixed(2) }}
          </p>
        </div>
      </div>
    </div>
    <SButton
      v-if="isInOrderStep || isInPaymentStep"
      type="button"
      variant="outline"
      size="lg"
      :class="[
        'w-full keyboard-button',
        { hidden: calcVisibility && isInOrderStep },
      ]"
      :disabled="!isInOrderStep"
      @click="calcVisibility = !calcVisibility"
    >
      <SvgIcon
        :iconContent="calcIcon"
        :iconAlt="calcAlt"
        :iconStyle="calcIconStyle"
      />
    </SButton>
    <div class="calculator-wrapper" v-if="calcVisibility && isInOrderStep">
      <numeric-calculator
        :visible="calcVisibility"
        @closeCalc="closeCalculator"
      ></numeric-calculator>
    </div>

    <div v-if="showCancelTicketsModal" class="cancel-tickets-popup">
      <SDialog
        :open="showCancelTicketsModal"
        @update:open="(open) => !open && closeModal()"
      >
        <SDialogContent
          class="cancel-tickets-dialog !w-[960px] !max-w-[960px] !max-h-[85vh] !p-0 overflow-hidden"
          :class="
            selectedConfirmedCount > 0
              ? '!grid-rows-[auto_minmax(0,1fr)_auto]'
              : '!grid-rows-[auto_minmax(0,1fr)]'
          "
          @escape-key-down.prevent
          @pointer-down-outside.prevent
          @open-auto-focus.prevent
        >
          <SDialogHeader>
            <SDialogTitle>Tickets Cancellation</SDialogTitle>
          </SDialogHeader>
          <div class="confirmed-tickets-container min-h-0 overflow-y-auto">
            <el-row class="confirmed-tickets-header sticky top-0 z-10">
              <el-col :span="1" class="d-flex fl-col fl-j-c">
                <SCheckbox
                  :model-value="isAllConfirmedSelected"
                  :disabled="isRemoveButtonLoading"
                  data-cy="confirm-remove-pss-select-all"
                  @update:model-value="toggleSelectAllConfirmed"
                />
              </el-col>
              <el-col
                :span="confirmedOrderLineItemsToDelete[0]?.seat ? 6 : 7"
                >{{ t("common.ticket-name") }}</el-col
              >
              <el-col
                v-if="confirmedOrderLineItemsToDelete[0]?.seat"
                :span="3"
                >{{ t("field.seat") }}</el-col
              >
              <el-col :span="4">{{ t("common.price-name") }}</el-col>
              <el-col :span="4">{{ t("common.price-value") }}</el-col>
              <el-col
                :span="confirmedOrderLineItemsToDelete[0]?.seat ? 5 : 7"
                >{{ t("common.bar-code") }}</el-col
              >
              <el-col :span="1"></el-col>
            </el-row>

            <div
              v-for="(orderLine, index) in confirmedOrderLineItemsToDelete"
              :key="orderLine.id + index"
            >
              <el-row
                class="confirmed-ticket-row"
                :class="{
                  'confirmed-ticket-row-selected': isConfirmedSelected(
                    orderLine.tokenObject.id
                  ),
                }"
                :style="
                  index !== confirmedOrderLineItemsToDelete.length - 1
                    ? 'border-bottom: 1px solid #d5d5d5'
                    : ''
                "
              >
                <el-col :span="1" class="d-flex fl-col fl-j-c">
                  <SCheckbox
                    :model-value="isConfirmedSelected(orderLine.tokenObject.id)"
                    :disabled="isRemoveButtonLoading"
                    data-cy="confirm-remove-pss-select-one"
                    @update:model-value="
                      () => toggleConfirmedSelection(orderLine.tokenObject.id)
                    "
                  />
                </el-col>
                <el-col
                  class="confirmed-ticket-name d-flex fl-col fl-j-c"
                  :span="orderLine.seat ? 6 : 7"
                >
                  <p>{{ orderLine.categoryName }}</p>
                </el-col>
                <el-col
                  v-if="orderLine.seat"
                  class="confirmed-ticket-name d-flex fl-col fl-j-c"
                  :span="3"
                >
                  <p>{{ orderLine.seat.seatLabel }}</p>
                </el-col>
                <el-col :span="4" class="d-flex fl-col fl-j-c">
                  <p>{{ orderLine.priceName }}</p>
                </el-col>
                <el-col :span="4" class="d-flex fl-col fl-j-c">
                  <p class="confirmed-ticket-price">
                    {{
                      currency +
                      " " +
                      (orderLine.overridePrice ?? orderLine.unitPrice).toFixed(
                        2
                      )
                    }}
                  </p>
                </el-col>
                <el-col
                  :span="orderLine.seat ? 5 : 7"
                  class="d-flex fl-col fl-j-c"
                >
                  <p>{{ orderLine.barCode ?? "-" }}</p>
                </el-col>
                <el-col
                  :span="1"
                  class="confirmed-ticket-delete d-flex fl-col fl-j-c"
                >
                  <div>
                    <SButton
                      type="button"
                      variant="ghost"
                      size="icon"
                      class="confirmed-ticket-delete-btn text-destructive"
                      :loading="isRemoveButtonLoading"
                      :disabled="isRemoveButtonLoading"
                      aria-label="Cancel ticket"
                      data-cy="confirm-remove-pss-button"
                      @click="openDeleteOrderLineMessageBox(orderLine)"
                    >
                      <Trash2Icon />
                    </SButton>
                  </div>
                </el-col>
              </el-row>
            </div>
          </div>
          <SDialogFooter v-if="selectedConfirmedCount > 0">
            <div class="confirmed-tickets-bulk-footer">
              <p class="confirmed-tickets-bulk-count">
                {{ selectedConfirmedCount }} ticket(s) selected
              </p>
              <SButton
                type="button"
                class="confirmed-tickets-bulk-btn"
                :loading="isRemoveButtonLoading"
                :disabled="isRemoveButtonLoading"
                data-cy="bulk-remove-pss-button"
                @click="bulkCancelSelectedTickets"
              >
                Cancel selected tickets
              </SButton>
            </div>
          </SDialogFooter>
        </SDialogContent>
      </SDialog>
    </div>

    <div v-if="showCancelConfirmationModal" class="cancel-confirmation-popup">
      <SDialog
        :open="showCancelConfirmationModal"
        @update:open="(open) => !open && closeCancelConfirmationModal()"
      >
        <SDialogContent
          :show-close-button="false"
          class="cancel-confirmation-dialog !w-[450px] !max-w-[450px] !p-0"
          data-cy="cancel-tickets-confirmation-modal"
          @escape-key-down.prevent
          @pointer-down-outside.prevent
        >
          <SDialogHeader>
            <SDialogTitle class="sr-only">Cancel ticket(s)?</SDialogTitle>
          </SDialogHeader>
          <div class="cancel-confirmation-content">
            <div class="cancel-confirmation-icon-wrapper">
              <SvgIcon
                :iconContent="alertTriangleIcon"
                :iconAlt="'alert'"
                class="cancel-confirmation-icon"
              />
            </div>
            <h3 class="cancel-confirmation-title">Cancel ticket(s)?</h3>
            <p class="cancel-confirmation-text">
              Are you sure you want to cancel {{ cancelConfirmationCount }}
              ticket(s)? This action cannot be turned back.
            </p>
            <div class="cancel-confirmation-actions">
              <SButton
                type="button"
                variant="outline"
                class="cancel-confirmation-btn-secondary"
                data-cy="cancel-tickets-confirmation-cancel-button"
                @click="closeCancelConfirmationModal"
              >
                {{ t("button.cancel") }}
              </SButton>
              <SButton
                type="button"
                class="cancel-confirmation-btn-primary"
                :loading="isRemoveButtonLoading"
                :disabled="isRemoveButtonLoading"
                data-cy="cancel-tickets-confirmation-ok-button"
                @click="confirmTicketsCancellation"
              >
                {{ t("availability-info.ok-button") }}
              </SButton>
            </div>
          </div>
        </SDialogContent>
      </SDialog>
    </div>

    <!-- Waiver modals -->
    <LinkWaiverModal />
    <ShareWaiverModal />
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, provide, ref, watch } from "vue";
import { storeToRefs } from "pinia";
import { useI18n } from "vue-i18n";
import {
  Currency,
  LineAddonType,
  LineType,
  OrderLine,
  OrderLinePublicType,
  OrderLineSeat,
  OrderSessionState,
  OrderToken,
  Payment,
  PaymentMethod,
  SeatType,
  TokenStatus,
  UnitItem,
} from "@smeetz/orders";
import { Discount } from "@smeetz/order-common";
import { useOrderStore } from "@smeetz/order-store";
import NumericCalculator from "./NumericCalculator.vue";
import WaiverSection from "@/components/layout/aside-order-summary/WaiverSection.vue";
import LinkWaiverModal from "@/components/modal/waivers/LinkWaiverModal.vue";
import ShareWaiverModal from "@/components/modal/waivers/ShareWaiverModal.vue";
import { setupAsideOrderSummary } from "./aside-order-summary";
import {
  SBadge,
  SButton,
  SCheckbox,
  SDialog,
  SDialogContent,
  SDialogFooter,
  SDialogHeader,
  SDialogTitle,
} from "@smeetz/ui-core";
import { Trash2Icon } from "@smeetz/ui-icons";
import copyIcon from "@/assets/images/svg/copy.svg";
import check from "@/assets/images/svg/green-check.svg";
import alertTriangleIcon from "@/assets/images/svg/alert-triangle.svg";
import { useOrderMetaStore } from "@/stores/order-meta.store";
import { useGroupOffersStore } from "@/stores/group-offers.store";
import { getTimeslotById } from "@/dexie/timeslots";
import {
  isOrderEdited,
  removeOrderLines,
  cancelLinesByToken,
  removeDiscount,
} from "@/utils/vue-helpers/orders.helper";
import {
  getDisplayedPayments,
  getLineStatusTranslation as buildLineStatusTranslation,
  getOrderLineDiscountLines as buildOrderLineDiscountLines,
  getOrderLineDiscountedTotal,
  getOrderLineDisplayName,
  getPaymentCurrencies,
  getPendingVoucherRows,
  shouldShowTicketInfo,
} from "@/utils/vue-helpers/order-summary-display";
import { openInternetNeededMessageBox } from "@/utils/vue-helpers/common";
import { LogLevel, LogService } from "@/services/logging";
import { PaymentMethodToTranslatedString } from "@/utils/vue-helpers/pos-config";
import { formatDateTime } from "@/utils/helpers/dates";
import { BookingStep } from "@/constants/booking";
import { previousVisitsOpenBalanceOf } from "@/utils/helpers/open-balance";
import { LINKED_WAIVERS_KEY } from "@/components/layout/aside-order-summary/waiver-keys";

interface OrderLineDeleteItem extends OrderLine {
  categoryName: string;
  priceName: string;
  barCode: string;
  priceValue: number;
  tokenObject: OrderToken;
  unitItem?: UnitItem;
  seat?: OrderLineSeat;
}

export default defineComponent({
  components: {
    NumericCalculator,
    WaiverSection,
    LinkWaiverModal,
    ShareWaiverModal,
    SBadge,
    SButton,
    SCheckbox,
    SDialog,
    SDialogContent,
    SDialogFooter,
    SDialogHeader,
    SDialogTitle,
    Trash2Icon,
  },
  name: "AsideOrderSummary",
  setup() {
    const {
      showCancelTicketsModal,
      isLongTitle,
      iconContent,
      iconAlt,
      iconStyle,
      printIcon,
      printAlt,
      isWaiverAllowed,
      getCustomerFullName,
      onClickCustomers,
      calcVisibility,
      calcIcon,
      calcIconStyle,
      calcAlt,
      closeCalculator,
      closeModal,
      groupCurrency, // FIXME:
      isRemoveButtonLoading,
      copyToClipboard,
      showCopyCheck,
      printBill,
      showPrintBill,
      linkedCount,
      linkedWaiverIds,
      isLoadingCount,
      refreshLinkedWaivers,
    } = setupAsideOrderSummary();

    const { t } = useI18n({});

    const orderStore = useOrderStore();
    const {
      selectedOrder,
      selectedOrderSummary: orderSummary,
      allOrderLines,
    } = storeToRefs(orderStore);

    const orderMetaStore = useOrderMetaStore();
    const { step, isInOrderStep, isInPaymentStep, isInCompletedStep } =
      storeToRefs(orderMetaStore);

    // FIS-719: debt left open on the order (0 when none).
    const openBalance = computed(() => orderSummary.value.openBalance);
    // FIS-719: what the cashier collects now (session balance + open debt).
    const amountDue = computed(() => orderSummary.value.amountDue);
    // FIS-719: the open-balance row. Before completion it shows only debt
    // carried by OTHER sessions (a debt deferred on this visit is not a
    // previous visit's); on the Complete step it shows the order's open
    // balance, labelled "Remaining balance".
    const openBalanceRowAmount = computed(() =>
      isInCompletedStep.value
        ? openBalance.value
        : previousVisitsOpenBalanceOf(
            orderSummary.value,
            selectedOrder.value?.orderSession?.id
          )
    );
    const orderLineToCancel = ref<OrderLine | null>(null);

    // Group offers — used to resolve offer names for OFFER-type discounts in
    // per-line discount labels. Sourced from the persisted group-offers store.
    const groupOffersStore = useGroupOffersStore();
    const { offers: groupOffers } = storeToRefs(groupOffersStore);

    const confirmedOrderLineItemsToDelete = computed<OrderLineDeleteItem[]>(
      () => {
        const orderLine = orderLineToCancel.value;
        if (!orderLine) {
          return [];
        }

        // Build a set of raw order line IDs that belong to this recap entry.
        // A token may reference the recap line directly, or a raw line whose
        // mainLineId points to the recap line (e.g. lines from other sessions).
        const relatedLineIds = new Set<string>([orderLine.id]);
        for (const raw of allOrderLines.value) {
          if (raw.mainLineId === orderLine.id) {
            relatedLineIds.add(raw.id);
          }
        }

        const tokens = (selectedOrder.value?.tokens ?? []).filter(
          (t) =>
            relatedLineIds.has(t.orderLineId) && t.status === TokenStatus.ACTIVE
        );

        return tokens.map((token) => {
          const unitItem = token.unitItemId
            ? orderLine.unitItems?.find((ui) => ui.id === token.unitItemId)
            : undefined;

          const seat = token.unitItemId
            ? orderLine.seats?.find((s) => s.unitItemId === token.unitItemId)
            : orderLine.seats?.find((s) => s.type === SeatType.GA);

          return {
            ...orderLine,
            barCode: token.barcode ?? "",
            priceValue: orderLine.overridePrice ?? orderLine.unitPrice,
            tokenObject: token,
            unitItem: unitItem as UnitItem | undefined,
            seat,
          } as OrderLineDeleteItem;
        });
      }
    );

    // --- Bulk multi-select cancellation (APPS-287) ---
    // Selection is keyed by each row's cancel token id (tokenObject.id).
    const selectedConfirmedTokens = ref(new Set<string>());

    // Custom cancel-confirmation modal (replaces legacy ElMessageBox.confirm).
    // `pendingCancelTokens` holds the token ids queued for cancellation,
    // populated by both the per-row trash action and the bulk action.
    const showCancelConfirmationModal = ref(false);
    const pendingCancelTokens = ref<string[]>([]);

    const cancelConfirmationCount = computed(
      () => pendingCancelTokens.value.length
    );

    const selectedConfirmedCount = computed(
      () => selectedConfirmedTokens.value.size
    );

    const isAllConfirmedSelected = computed(() => {
      const items = confirmedOrderLineItemsToDelete.value;
      return (
        items.length > 0 &&
        items.every((item) =>
          selectedConfirmedTokens.value.has(item.tokenObject.id)
        )
      );
    });

    function isConfirmedSelected(tokenId: string) {
      return selectedConfirmedTokens.value.has(tokenId);
    }

    function toggleConfirmedSelection(tokenId: string) {
      // Reassign a new Set so the ref reactivity triggers dependent computeds.
      const next = new Set(selectedConfirmedTokens.value);
      if (next.has(tokenId)) {
        next.delete(tokenId);
      } else {
        next.add(tokenId);
      }
      selectedConfirmedTokens.value = next;
    }

    function toggleSelectAllConfirmed() {
      if (isAllConfirmedSelected.value) {
        selectedConfirmedTokens.value = new Set<string>();
        return;
      }
      selectedConfirmedTokens.value = new Set(
        confirmedOrderLineItemsToDelete.value.map((item) => item.tokenObject.id)
      );
    }

    function bulkCancelSelectedTickets() {
      if (selectedConfirmedTokens.value.size === 0) {
        return;
      }
      pendingCancelTokens.value = Array.from(selectedConfirmedTokens.value);
      showCancelConfirmationModal.value = true;
    }

    function closeCancelConfirmationModal() {
      showCancelConfirmationModal.value = false;
      pendingCancelTokens.value = [];
    }

    function confirmTicketsCancellation() {
      const tokenIds = pendingCancelTokens.value;
      LogService.info(`User confirmed ticket cancellation`, {
        params: tokenIds,
      });
      isRemoveButtonLoading.value = true;
      cancelLinesByToken(tokenIds)
        .then(() => {
          selectedConfirmedTokens.value = new Set<string>();
          pendingCancelTokens.value = [];
          showCancelConfirmationModal.value = false;
        })
        .catch(() => {
          LogService.info(
            `Ticket cancellation failed`,
            { params: tokenIds },
            LogLevel.Error
          );
        })
        .finally(() => {
          isRemoveButtonLoading.value = false;
        });
    }

    watch(confirmedOrderLineItemsToDelete, (items) => {
      // Clear any stale selection whenever the underlying list changes.
      selectedConfirmedTokens.value = new Set<string>();
      if (items.length === 0) {
        showCancelTicketsModal.value = false;
      }
    });

    const orderLineTimeslots = ref(new Map<number, string>());

    watch(
      () => orderSummary.value?.orderLines,
      async (allOrderLines) => {
        if (!allOrderLines || !allOrderLines.length) {
          orderLineTimeslots.value.clear();
          return;
        }

        for (const line of allOrderLines) {
          if (
            line.timeSlotId &&
            !orderLineTimeslots.value.has(line.timeSlotId)
          ) {
            const timeslot = await getTimeslotById(line.timeSlotId);
            if (timeslot) {
              orderLineTimeslots.value.set(
                line.timeSlotId,
                formatDateTime(timeslot.startDateTime)
              );
            }
          }
        }
      },
      { immediate: true }
    );

    const currenciesUsedInPayment = computed(() =>
      getPaymentCurrencies(orderSummary.value)
    );

    function shouldShowRemoveBtn(
      orderLine: OrderLine & { state: OrderSessionState }
    ): boolean {
      if (step.value !== BookingStep.Order) {
        // remove button is displayed only in Order step
        return false;
      }
      if (orderLine.addonType === LineAddonType.AUTO_INCLUDED) {
        return false;
      }
      if (orderLine.state === OrderSessionState.ABANDONED) {
        return false;
      }
      if (
        orderLine.state === OrderSessionState.COMPLETED &&
        orderLine.lineType === LineType.CancelLine
      ) {
        return false;
      }
      if (
        orderLine.itemType === OrderLinePublicType.RETAIL ||
        orderLine.itemType === OrderLinePublicType.VOUCHER ||
        orderLine.itemType === OrderLinePublicType.MEMBERSHIP ||
        orderLine.itemType === OrderLinePublicType.MEMBERSHIP_VOUCHER
      ) {
        return true;
      }
      // TODO: if (isInThePast(pss) && pss.bookingStatus === BookingStatus.Confirmed) {
      return true;
    }

    function shouldShowRemovePaymentBtn(payment: Payment) {
      if (step.value === BookingStep.CompleteOrder) {
        return false;
      }

      if (payment.paymentMethod === PaymentMethod.POS_ADYEN) {
        return false;
      }

      // Only payments added in the current editing session are removable;
      // payments from a previously-completed session stay locked.
      const activeSessionId = selectedOrder.value?.orderSession?.id;
      if (payment.orderSessionId !== activeSessionId) {
        return false;
      }

      return true;
    }

    function handleRemovePayment(payment: Payment) {
      console.error("method not implemented", payment);
    }

    async function handleRemoveOrderLine(
      orderLine: OrderLine & { state: OrderSessionState }
    ) {
      if (orderLine.state === OrderSessionState.PENDING) {
        try {
          await removeOrderLines([orderLine.id]);
        } catch {
          // journaled + surfaced by the order helper; the line stays listed
        }
        return;
      }

      if (orderLine.state === OrderSessionState.COMPLETED) {
        orderLineToCancel.value = orderLine;
        showCancelTicketsModal.value = true;
        return;
      }
    }

    function openDeleteOrderLineMessageBox(orderLine: OrderLineDeleteItem) {
      // If the user is not connected and he wants to cancel a seating PSS then we stop him
      // because we don't want him to be able to cancel the pss and leave the seat booked in seats io
      const isOnline = ref(window.navigator.onLine);
      if (
        !isOnline.value &&
        orderLine.itemType === OrderLinePublicType.SEATED
      ) {
        openInternetNeededMessageBox();
        return;
      }

      pendingCancelTokens.value = [orderLine.tokenObject.id];
      showCancelConfirmationModal.value = true;
    }

    function getLineStatusTranslation(orderLine: OrderLine) {
      return buildLineStatusTranslation(orderLine, t);
    }

    function getOrderLineDiscountLines(
      orderLine: OrderLine & { discounts?: Discount[] }
    ): string[] {
      return buildOrderLineDiscountLines(
        orderLine,
        orderSummary.value,
        groupOffers.value,
        t
      );
    }

    const displayedPayments = computed(() =>
      getDisplayedPayments(orderSummary.value)
    );

    const pendingVoucherPayments = computed(() =>
      getPendingVoucherRows(orderSummary.value)
    );

    /**
     * Total amount confirmed against the order — payments plus applied
     * vouchers. `totalPayments` is in cents; `totalVouchers` in major units.
     */
    const totalConfirmedAmount = computed(
      () =>
        orderSummary.value.totalPayments / 100 +
        orderSummary.value.totalVouchers
    );

    async function removeVoucherPayment(voucherId: string) {
      if (!voucherId) return;
      try {
        await removeDiscount(voucherId);
      } catch (error) {
        LogService.info("Voucher removal failed", { error }, LogLevel.Error);
      }
    }

    function shouldShowRemoveVoucherBtn(): boolean {
      return step.value !== BookingStep.CompleteOrder;
    }

    provide(LINKED_WAIVERS_KEY, {
      linkedWaiverIds,
      refresh: refreshLinkedWaivers,
    });

    return {
      showCancelTicketsModal,
      iconContent,
      iconAlt,
      iconStyle,
      printIcon,
      printAlt,
      t,
      currency: selectedOrder.value?.orderSession?.currency || Currency.CHF,
      isWaiverAllowed,
      BookingStep,
      isInOrderStep,
      isInPaymentStep,
      isInCompletedStep,
      getCustomerFullName,
      isLongTitle,
      onClickCustomers,
      isOrderEdited,
      calcVisibility,
      calcIcon,
      calcIconStyle,
      calcAlt,
      closeCalculator,
      closeModal,
      groupCurrency,
      currenciesUsedInPayment,
      isRemoveButtonLoading,
      copyIcon,
      copyToClipboard,
      check,
      showCopyCheck,
      printBill,
      showPrintBill,
      selectedOrder,
      orderSummary,
      openBalance,
      amountDue,
      openBalanceRowAmount,
      displayedPayments,
      pendingVoucherPayments,
      totalConfirmedAmount,
      removeVoucherPayment,
      shouldShowRemoveVoucherBtn,
      step,
      OrderSessionState,
      LineType,
      OrderLinePublicType,
      orderLineTimeslots,
      getOrderLineDisplayName,
      shouldShowTicketInfo,
      shouldShowRemoveBtn,
      shouldShowRemovePaymentBtn,
      handleRemovePayment,
      handleRemoveOrderLine,
      getOrderLineDiscountLines,
      getOrderLineDiscountedTotal,
      confirmedOrderLineItemsToDelete,
      selectedConfirmedCount,
      isAllConfirmedSelected,
      isConfirmedSelected,
      toggleConfirmedSelection,
      toggleSelectAllConfirmed,
      bulkCancelSelectedTickets,
      openDeleteOrderLineMessageBox,
      showCancelConfirmationModal,
      cancelConfirmationCount,
      closeCancelConfirmationModal,
      confirmTicketsCancellation,
      alertTriangleIcon,
      getLineStatusTranslation,
      PaymentMethodToTranslatedString,
      linkedCount,
      isLoadingCount,
    };
  },
});
</script>

<style lang="scss">
@import "@/styles/variables.scss";

$summary-width: 390px;

.el-aside {
  width: unset;
}

.order-summary-wrapper {
  flex-grow: 3;
  width: $summary-width;
  // padding: 5px 15px;
  padding-top: 5px;
  padding-bottom: 5px;
  border-radius: $border-radius;
  background-color: #313335;
  color: white;

  .remove-btn {
    --s-icon-size: 17px;
    color: $color-red;
    cursor: pointer;
  }

  .order-summary {
    padding-left: 15px;
    padding-right: 15px;
    .upper-part-container {
      height: 62px;
      min-height: 62px;

      .title {
        text-align: left;
        letter-spacing: 0px;
        color: white;
        font-weight: 700;
        font-style: normal;
        font-size: 22px;
        padding-top: 15px;
      }

      .max-content-width {
        width: max-content;
      }

      .long-title {
        font-size: 16px;
        line-height: 20px;
      }

      .assign-customer-btn-container,
      .print-receipt-btn-container {
        cursor: pointer;
        line-height: 15px;
        letter-spacing: 0px;
        color: white;
        font-weight: 400;
        font-style: normal;
        font-size: 11px;
        text-align: center;
        padding-left: 10px;

        .icon-container {
          margin-right: 5px;
        }
      }
    }

    .pss-list-container {
      padding-top: 10px;
      padding-bottom: 10px;

      .pss-container {
        // height: fit-content;
        padding-top: 5px;
        padding-bottom: 5px;
        min-height: 80px;
        align-items: center;
        border-bottom: 1px solid white;

        .ticket-name-container {
          width: 50%;
          text-align: left;

          .ticket-name {
            font-weight: 700;
            font-style: normal;
            font-size: 14px;
            line-height: 14px;
            margin-bottom: 5px;
          }
          .price-name,
          .ticket-discount-info {
            font-weight: 400;
            font-style: normal;
            font-size: 10px;
            line-height: 14px;
            color: #959595;
            font-weight: 400;
          }
          .timeslot {
            font-weight: 400;
            font-style: normal;
            font-size: 10px;
            line-height: 14px;
          }
          .seat {
            font-weight: 400;
            font-style: normal;
            font-size: 10px;
            line-height: 14px;
            overflow: hidden;
            white-space: nowrap;
            text-overflow: ellipsis;
          }
        }

        .quantity-container {
          width: 10%;
          text-align: center;
          display: flex;
          flex-direction: row;
          justify-content: center;
          align-items: center;

          .quantity {
            font-weight: 700;
            font-style: normal;
            font-size: 12px;
            text-align: right;
            line-height: 14px;
            letter-spacing: 0px;
            margin-right: 8px;
            white-space: nowrap;
          }
        }

        .unit-price-container {
          width: 20%;
          text-align: center;

          .unit-price {
            font-weight: 700;
            font-style: normal;
            font-size: 12px;
            text-align: right;
            line-height: 14px;
            letter-spacing: 0px;
          }
        }
        .total-price-container.with-pt {
          padding-top: 1.2rem;
        }
        .total-price-container {
          width: 20%;
          text-align: right;
          display: flex;
          flex-direction: column;
          gap: 0.5rem;

          .total-price {
            font-weight: 700;
            font-style: normal;
            font-size: 12px;
            text-align: right;
            line-height: 14px;
            letter-spacing: 0px;
          }
        }

        .erased-tickets {
          text-decoration: line-through;
        }
      }

      .tickets-status-container {
        font-weight: 700;
        font-style: normal;
        font-size: 12px;
        line-height: 12px;
        text-align: right;
        width: 100%;
        color: yellow;
        margin-top: -15px;
        padding-bottom: 5px;
      }

      .cancelled-pss-container {
        background-color: rgba(
          $color: $color-grid-button-purple /*$color-dark-grey-2 */,
          $alpha: 0.5
        );
      }
    }

    .total-container {
      line-height: 30px;
      height: 30px;
      font-weight: 700;
      font-style: normal;
      font-size: 14px;
      // padding-left: 1%;
    }

    .tax-container {
      border-bottom: 1px solid white;
    }

    .discount-container {
      font-size: 12px;
      line-height: 30px;
      height: 30px;

      img {
        height: 17px;
        width: 17px;
        cursor: pointer;
      }
    }

    .confirmed-amount-container {
      border-bottom: 1px solid white;
      border-top: 1px solid white;

      .confirmed-amount {
        height: 40px;
        font-weight: 700;
        font-style: normal;
        font-size: 14px;
        line-height: 14px;
        margin-bottom: 5px;

        img {
          height: 17px;
          width: 17px;
          cursor: pointer;
        }
      }
    }

    .confirmed-amount-total {
      font-weight: 700;
      font-style: normal;
      font-size: 14px;
      // text-align: right;
      line-height: 50px;
      // height: 50px;
      letter-spacing: 0px;
    }
  }

  .accountings-container {
    height: 45px;
    border-top: 2px solid white;
    padding-left: 15px;
    padding-right: 15px;

    .title {
      text-align: left;
    }
    .value {
      text-align: right;
    }
    .title,
    .value {
      font-weight: 700;
      font-style: normal;
      font-size: 16px;
      text-align: left;
      line-height: 20px;
      letter-spacing: 0px;
    }
  }
}

.discount-hundreds {
  padding-left: 2%;
}

.discount-dozens {
  padding-left: 4%;
}

.confirmed-ones {
  padding-left: 3%;
}

.confirmed-dozens {
  padding-left: 5%;
}

.confirmed-hundreds {
  padding-left: 1%;
}

.keyboard-button {
  margin-top: 13px;
  padding: 24px;
  width: 390px;
  height: 45px;
  background: rgba(75, 155, 165, 0.05);
  border: 1px solid rgba(51, 51, 51, 0.25);
  border-radius: 4px;
}

.calculator-wrapper {
  margin-top: 6px;
  border: 1px solid rgba(51, 51, 51, 0.15);
  border-radius: 5px;
}

.aside-wrapper {
  width: 390px;
  height: 72vh;
  min-height: 100%;
}

.cancel-tickets-dialog {
  border-radius: 6px;

  [data-slot="dialog-header"] {
    padding: 14px 16px 8px;
    border-bottom: 0;
  }

  [data-slot="dialog-title"] {
    font-size: 20px;
    line-height: 26px;
    font-weight: 700;
    color: #141a23;
  }

  [data-slot="dialog-close"] {
    color: #586473;
    font-size: 16px;
    font-weight: 400;
  }

  [data-slot="dialog-footer"] {
    border-top: 1px solid #d5d5d5;
    padding: 6px 12px;
    margin-top: 0;
  }

  .confirmed-tickets-container {
    padding: 0 16px;
    border-bottom: 1px solid #d5d5d5;
    scrollbar-width: thin;
    scrollbar-color: #8f8f8f transparent;
  }

  .confirmed-tickets-container::-webkit-scrollbar {
    width: 6px;
  }

  .confirmed-tickets-container::-webkit-scrollbar-track {
    background: transparent;
    margin: 0;
    border: 0;
  }

  .confirmed-tickets-container::-webkit-scrollbar-thumb {
    background: #8f8f8f;
    border-radius: 8px;
  }

  .confirmed-tickets-header {
    font-size: 12px;
    font-weight: 700;
    border-bottom: 1px solid #d5d5d5;
    padding: 6px 12px 8px;
    color: #617085;
    background: #fff;
    align-items: center;
  }

  .confirmed-ticket-row {
    padding: 0 12px;
    transition: background-color 0.15s ease;
    min-height: 58px;
    color: #141a23;
    font-size: 11px;
    align-items: center;
  }

  .confirmed-ticket-row-selected {
    background: #ebeff4;
  }

  .confirmed-ticket-name {
    font-weight: 700;
  }

  .confirmed-ticket-price {
    font-weight: 700;
  }

  .confirmed-ticket-row .el-col {
    min-height: 58px;
    line-height: 16px;
  }

  .confirmed-ticket-row p {
    margin: 0;
  }

  .confirmed-ticket-delete {
    text-align: right;
    padding-right: 2px;
  }

  .confirmed-ticket-delete-btn {
    color: #ff5b59;
    border: 0;
    padding: 0;
    width: 18px;
    height: 18px;
    min-height: 18px;
    border-radius: 0;
    background: transparent;
  }

  .confirmed-ticket-delete-btn:hover,
  .confirmed-ticket-delete-btn:focus {
    color: #ff5b59;
    background: transparent;
  }

  .confirmed-tickets-bulk-footer {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    min-height: 46px;
    padding: 0;
  }

  .confirmed-tickets-bulk-count {
    font-size: 12px;
    line-height: 16px;
    color: #141a23;
    margin: 0;
    font-weight: 500;
  }

  .confirmed-tickets-bulk-btn {
    background: #0f9b8e !important;
    border: 1px solid #0f9b8e !important;
    color: white !important;
    border-radius: 8px;
    min-height: 34px;
    padding: 0 16px;
    font-size: 12px;
    line-height: 16px;
    font-weight: 500;
  }

  .confirmed-tickets-bulk-btn:hover,
  .confirmed-tickets-bulk-btn:focus {
    background: #0f9b8e !important;
    border-color: #0f9b8e !important;
    color: white !important;
  }

  .confirmed-tickets-bulk-btn.is-disabled,
  .confirmed-tickets-bulk-btn.is-disabled:hover {
    background: #0f9b8e !important;
    border-color: #0f9b8e !important;
    color: white !important;
    opacity: 0.45;
  }

  .el-checkbox__inner {
    border-color: #0f9b8e;
    width: 14px;
    height: 14px;
  }

  .el-checkbox {
    display: inline-flex;
    align-items: center;
  }

  .el-checkbox__input.is-checked .el-checkbox__inner,
  .el-checkbox__input.is-indeterminate .el-checkbox__inner {
    background-color: #0f9b8e;
    border-color: #0f9b8e;
  }
}

.cancel-confirmation-dialog {
  border-radius: 10px;
  max-width: 430px;

  [data-slot="dialog-header"] {
    display: none;
  }

  .cancel-confirmation-content {
    padding: 20px 22px 16px;
    text-align: center;
  }

  .cancel-confirmation-icon-wrapper {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: #fdeecf;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 8px;
  }

  .cancel-confirmation-icon {
    width: 18px;
    height: 18px;
  }

  .cancel-confirmation-title {
    font-size: 18px;
    line-height: 24px;
    font-weight: 700;
    color: #141a23;
    margin: 0 0 8px;
  }

  .cancel-confirmation-text {
    font-size: 13px;
    line-height: 20px;
    color: #65758c;
    margin: 0;
  }

  .cancel-confirmation-actions {
    margin-top: 14px;
    display: flex;
    justify-content: center;
    gap: 8px;
  }

  .cancel-confirmation-btn-secondary,
  .cancel-confirmation-btn-primary {
    min-height: 36px;
    padding: 0 18px;
    border-radius: 7px;
    font-size: 14px;
    line-height: 18px;
    font-weight: 500;
  }

  .cancel-confirmation-btn-secondary {
    border: 1px solid #d8dee8;
    color: #141a23;
    background: #fff;
  }

  .cancel-confirmation-btn-primary {
    background: #10998d !important;
    border: 1px solid #10998d !important;
    color: #fff !important;
  }
}

.amounts-paid {
  font-weight: 800;
  font-style: normal;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0px;
}

.pending-payment-hint {
  font-weight: 400;
  font-size: 11px;
  line-height: 14px;
  color: #959595;
}

.order-reference {
  font-weight: 400;
  font-style: normal;
  font-size: 0.8rem;
  line-height: 14px;
  color: #959595;
  font-weight: 400;
}
.order-reference-wrapper {
  display: flex;
  align-items: center;
  gap: 5px;
}
.check {
  padding-bottom: 5px;
}
.clipboard {
  padding-bottom: 5px;
}
</style>
